import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { formatCurrency } from '../../utils/formatters';

// Categorias de despesa dedutíveis na declaração do IR
const DEDUCTIBLE = {
  saude: 'Despesas médicas (dedução integral)',
  educ:  'Despesas com educação (limite anual por dependente)',
};

function Section({ icon, title, subtitle, children }) {
  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(201,162,39,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className={`fas ${icon}`} style={{ color: 'var(--gold)', fontSize: 14 }}></i>
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Row({ label, sub, value, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid var(--border)', gap: 12 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: bold ? 700 : 500, color: 'var(--text)' }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>{sub}</div>}
      </div>
      <div style={{ fontSize: 13, fontWeight: bold ? 800 : 600, color: 'var(--text)', whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );
}

const Empty = ({ text }) => (
  <div style={{ textAlign: 'center', padding: '14px 0', color: 'var(--muted)', fontSize: 12 }}>{text}</div>
);

export default function TaxReport() {
  const { state } = useApp();
  const nowYear = new Date().getFullYear();
  const [baseYear, setBaseYear] = useState(nowYear - 1); // ano-base padrão: ano anterior

  const data = useMemo(() => {
    const yearTx = state.transactions.filter(t => t.date.startsWith(`${baseYear}-`) && t.status === 'paid');

    // Rendimentos por categoria
    const incomeByCat = {};
    yearTx.filter(t => t.type === 'income').forEach(t => {
      incomeByCat[t.categoryId] = (incomeByCat[t.categoryId] || 0) + t.value;
    });
    const incomeRows = Object.entries(incomeByCat)
      .map(([id, v]) => ({ cat: state.categories.find(c => c.id === id), value: v }))
      .sort((a, b) => b.value - a.value);
    const incomeTotal = incomeRows.reduce((s, r) => s + r.value, 0);

    // Despesas dedutíveis
    const deductibleRows = Object.keys(DEDUCTIBLE).map(catId => {
      const total = yearTx
        .filter(t => t.type === 'expense' && t.categoryId === catId)
        .reduce((s, t) => s + t.value, 0);
      return { catId, cat: state.categories.find(c => c.id === catId), total };
    }).filter(r => r.total > 0);
    const deductibleTotal = deductibleRows.reduce((s, r) => s + r.total, 0);

    // Transações dedutíveis detalhadas (para conferência)
    const deductibleTx = yearTx
      .filter(t => t.type === 'expense' && DEDUCTIBLE[t.categoryId])
      .sort((a, b) => a.date.localeCompare(b.date));

    return { incomeRows, incomeTotal, deductibleRows, deductibleTotal, deductibleTx };
  }, [state.transactions, state.categories, baseYear]);

  const yearOptions = [nowYear - 2, nowYear - 1, nowYear];

  return (
    <div className="page-enter">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Imposto de Renda</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>
            Declaração {baseYear + 1} — ano-base {baseYear}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {yearOptions.map(y => (
            <button
              key={y}
              onClick={() => setBaseYear(y)}
              style={{
                padding: '6px 14px', borderRadius: 99,
                border: baseYear === y ? '1px solid var(--gold)' : '1px solid var(--border)',
                background: baseYear === y ? 'rgba(201,162,39,0.12)' : 'var(--card-bg)',
                color: baseYear === y ? 'var(--gold)' : 'var(--muted)',
                fontWeight: baseYear === y ? 700 : 500, fontSize: 12, cursor: 'pointer',
              }}
            >
              {y}
            </button>
          ))}
          <button onClick={() => window.print()} className="btn btn-ghost" style={{ fontSize: 12 }}>
            <i className="fas fa-print"></i> Imprimir
          </button>
        </div>
      </div>

      {/* Disclaimer */}
      <div style={{ background: 'rgba(55,138,221,0.08)', border: '1px solid rgba(55,138,221,0.3)', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <i className="fas fa-circle-info" style={{ color: 'var(--blue)', marginTop: 2, flexShrink: 0 }}></i>
        <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
          Este relatório auxiliar é apenas <strong>para referência</strong> e reflete os dados lançados no MAW Finanças.
          Confira todas as informações com seus documentos oficiais (informes de rendimento, notas fiscais e recibos)
          antes de transmitir sua declaração à Receita Federal.
        </div>
      </div>

      {/* Rendimentos */}
      <Section icon="fa-sack-dollar" title="Rendimentos" subtitle={`Receitas registradas em ${baseYear}, agrupadas por categoria`}>
        {data.incomeRows.length === 0 ? (
          <Empty text={`Nenhuma receita registrada em ${baseYear}.`} />
        ) : (
          <>
            {data.incomeRows.map(r => (
              <Row key={r.cat?.id || 'x'} label={r.cat?.name || 'Sem categoria'} value={formatCurrency(r.value)} />
            ))}
            <Row label="Total de rendimentos" value={formatCurrency(data.incomeTotal)} bold />
          </>
        )}
      </Section>

      {/* Despesas dedutíveis */}
      <Section icon="fa-file-medical" title="Despesas Dedutíveis" subtitle="Saúde e educação — categorias aceitas pela Receita">
        {data.deductibleRows.length === 0 ? (
          <Empty text={`Nenhuma despesa de saúde ou educação registrada em ${baseYear}.`} />
        ) : (
          <>
            {data.deductibleRows.map(r => (
              <Row key={r.catId} label={r.cat?.name || r.catId} sub={DEDUCTIBLE[r.catId]} value={formatCurrency(r.total)} />
            ))}
            <Row label="Total dedutível" value={formatCurrency(data.deductibleTotal)} bold />
          </>
        )}
      </Section>

      {/* Detalhamento das dedutíveis */}
      {data.deductibleTx.length > 0 && (
        <Section icon="fa-list-check" title="Detalhamento para conferência" subtitle={`${data.deductibleTx.length} lançamentos dedutíveis em ${baseYear}`}>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {data.deductibleTx.map(t => {
              const [, m, d] = t.date.split('-');
              return (
                <Row
                  key={t.id}
                  label={t.description}
                  sub={`${d}/${m}/${baseYear} — ${state.categories.find(c => c.id === t.categoryId)?.name || ''}`}
                  value={formatCurrency(t.value)}
                />
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10 }}>
            <i className="fas fa-lightbulb" style={{ color: 'var(--gold)', marginRight: 5 }}></i>
            Guarde os recibos e notas fiscais de cada um destes lançamentos — a Receita pode solicitar comprovação por até 5 anos.
          </div>
        </Section>
      )}

      {/* Bens e direitos */}
      <Section icon="fa-chart-line" title="Bens e Direitos — Investimentos" subtitle="Posições cadastradas no módulo Investimentos">
        {state.investments.length === 0 ? (
          <Empty text="Nenhum investimento cadastrado." />
        ) : (
          <>
            {/* IR é declarado em R$ — ativos em US$ (usdV2) entram convertidos pelo câmbio do dia */}
            {state.investments.map(inv => {
              const conv = (v) => inv.currency === 'USD' ? (v || 0) * state.usdRate : (v || 0);
              return (
                <Row key={inv.id} label={inv.name || inv.type || 'Investimento'} sub={`${inv.type || ''}${inv.institution ? ` — ${inv.institution}` : ''}${inv.currency === 'USD' ? ' · em US$, convertido pelo câmbio do dia' : ''} · posição atual ${formatCurrency(conv(inv.current))}`} value={formatCurrency(conv(inv.invested))} />
              );
            })}
            <Row label="Total investido (custo de aquisição)" value={formatCurrency(state.investments.reduce((s, i) => s + (i.currency === 'USD' ? (i.invested || 0) * state.usdRate : (i.invested || 0)), 0))} bold />
          </>
        )}
      </Section>

      {/* Dívidas e ônus */}
      <Section icon="fa-file-invoice-dollar" title="Dívidas e Ônus Reais" subtitle="Dívidas cadastradas (declaráveis quando acima de R$ 5.000)">
        {state.debts.length === 0 ? (
          <Empty text="Nenhuma dívida cadastrada." />
        ) : (
          <>
            {state.debts.map(d => (
              <Row key={d.id} label={d.name || 'Dívida'} sub={d.settled ? 'Quitada' : `Em aberto — total original ${formatCurrency(d.total || 0)}`} value={formatCurrency(d.settled ? 0 : (d.remaining || 0))} />
            ))}
          </>
        )}
      </Section>
    </div>
  );
}
